/**
 * Notices a device clock that is far off (an iPad at 1 January 1970 refused every certificate and
 * computed wrong "today" values; Phase 1 C1). It compares the device's time with the `Date`
 * header of HTTP responses, which servers set from their own, synchronised clocks.
 */

/** Beyond this the clock is reported as off. HTTP dates have a resolution of one second. */
export const CLOCK_SKEW_LIMIT_MS = 5 * 60_000;

/** The median of this many recent observations is used, so that one server with a bad clock does not decide. */
const OBSERVATIONS = 5;

export interface ClockCheck {
    /** One response's `Date` header, and when the response arrived (device time). */
    observe(dateHeader: string | undefined, receivedAt: number) : void;
    /** Server time minus device time, in ms; undefined until a date has been seen. */
    skewMs() : number | undefined;
    /** Whether the device clock is off by more than `CLOCK_SKEW_LIMIT_MS`. */
    isOff() : boolean;
    subscribe(listener: () => void) : () => void;
}

export function createClockCheck() : ClockCheck {
    const skews: number[] = [];
    const listeners = new Set<() => void>();
    let wasOff = false;

    const median = () : number | undefined => {
        if (skews.length === 0) {
            return undefined;
        }

        const sorted = [...skews].sort((a, b) => a - b);

        return sorted[Math.floor(sorted.length / 2)];
    };
    const isOff = () : boolean => Math.abs(median() ?? 0) > CLOCK_SKEW_LIMIT_MS;

    return {
        observe(dateHeader, receivedAt) {
            const serverTime = dateHeader ? Date.parse(dateHeader) : Number.NaN;
            if (!Number.isFinite(serverTime)) {
                return;
            }

            skews.push(serverTime - receivedAt);
            if (skews.length > OBSERVATIONS) {
                skews.shift();
            }
            const off = isOff();
            if (off !== wasOff) {
                wasOff = off;
                for (const listener of [...listeners]) {
                    listener();
                }
            }
        },
        skewMs: median,
        isOff,
        subscribe(listener) {
            listeners.add(listener);

            return () => listeners.delete(listener);
        },
    };
}
